/**
 * "Load the older page when the old end of the thread comes into view."
 *
 * An observed SENTINEL rather than a container `scroll` listener, for two reasons:
 *
 * - **Attachment timing.** The scroll container is rendered by a child that a surface
 *   withholds while the channel row loads, so a listener attached on the parent's mount
 *   finds `null` and — with a static dep array — never retries. Keeping the observed node
 *   in STATE (set by a callback ref) re-runs the effect the moment the node commits, which
 *   is the whole point: there is no window in which the trigger is missing.
 * - **Layout independence.** The `flex-col-reverse` chat scroller and the natural-height
 *   `classic` thread put their old end at opposite visual edges and only one of them
 *   scrolls at all. An intersection against the viewport is clipped by whatever ancestor
 *   scroller exists, so neither the coordinate math nor the scrolling element has to be
 *   known here.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/** How far ahead of the old end the next page starts loading. */
const OLDER_END_ROOT_MARGIN = "200px";

export type UseLoadOlderOnVisibleOptions = {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  rootMargin?: string;
};

export function useLoadOlderOnVisible({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  rootMargin = OLDER_END_ROOT_MARGIN,
}: UseLoadOlderOnVisibleOptions): {
  sentinelRef: (node: HTMLDivElement | null) => void;
} {
  // State, not a ref: a ref write does not re-render, so the effect below would never
  // learn that the sentinel had appeared — exactly the bug this hook replaces.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinel(node);
  }, []);

  // Read the fetcher through a ref: its identity changes on every query render and must
  // not tear down and rebuild the observer.
  const fetchRef = useRef(fetchNextPage);
  fetchRef.current = fetchNextPage;

  useEffect(() => {
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchRef.current();
      },
      { rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasNextPage, isFetchingNextPage, rootMargin]);

  return { sentinelRef };
}
